import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subscription, catchError, forkJoin, map, of, switchMap, take } from 'rxjs';
import { CampaignManagerDto } from '@/app/common/general-types/campaigns.dto';
import { NoodleQuestionDto } from '@/app/common/general-types/noodle.dto';
import { MonitoringBriefResponseDto } from '@/app/common/general-types/social-screening.dto';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { BusinessAccountService } from '@/app/services/business-account/business-account.service';
import { ComplianceDocsService } from '@/app/services/compliance-docs/compliance-docs.service';
import { ImageService } from '@/app/services/image/image.service';
import { NoodleService } from '@/app/services/noodle/noodle.service';
import { ManageBriefTabsMonitorService } from '../../services/manage-brief-tabs-monitor/manage-brief-tabs-monitor';
import {
  ComplianceFact,
  MonitorBriefFacts,
  briefManagementView,
  isFindBriefContent,
} from '../mon-shell/monitor-shell';
import {
  ExtractedRuleChip,
  ExtractedRuleFilter,
  extractedRuleChips,
  extractedRuleCountClass,
  extractedRuleFilterClass,
  extractedRuleHasContext,
  extractedRuleNumberLabel,
  extractedRuleSummary,
  extractedRuleTag,
  questionsFromGroup,
  visibleExtractedRules,
} from './extracted-rules';

@Component({
  selector: 'mbtm-mon-brief-management',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LdgImgComponent],
  templateUrl: './mon-brief-management.html',
})
export class MonBriefManagement implements OnDestroy {
  readonly brief = input.required<MonitoringBriefResponseDto>();

  private readonly subscriptionList = new Subscription();
  private readonly postedByName = signal<string | null>(null);
  private readonly compliance = signal<ComplianceFact[]>([]);
  private readonly rules = signal<NoodleQuestionDto[]>([]);

  readonly ruleFilter = signal<ExtractedRuleFilter>('all');
  readonly rulesLoading = signal(true);

  private readonly businessAccountService = inject(BusinessAccountService);
  private readonly complianceDocsService = inject(ComplianceDocsService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly manageBriefTabsMonitorService = inject(ManageBriefTabsMonitorService);
  private readonly noodleService = inject(NoodleService);
  readonly imageService = inject(ImageService);

  readonly filterChips = computed(() => extractedRuleChips(this.rules()));
  readonly visibleRules = computed(() => visibleExtractedRules(this.rules(), this.ruleFilter()));
  readonly ruleSummary = computed(() => extractedRuleSummary(this.rules()));

  /** Find Brief Content briefs aren't checked against extracted rules, so the rules card is replaced. */
  readonly findsContentOnly = computed(() => isFindBriefContent(this.brief().monitorType));

  readonly view = computed(() =>
    briefManagementView(this.brief() as MonitorBriefFacts, this.postedByName(), this.compliance()),
  );

  constructor() {
    this.subscriptionList.add(
      toObservable(this.brief)
        .pipe(
          switchMap((brief) => {
            const facts = brief as MonitorBriefFacts;
            const updatedBy = facts.currentMonitoringKanbanProgress?.updatedBy ?? null;
            const docIds = facts.complianceDocIds ?? [];
            const brandId = this.currentUserService.focussedBrand?.id as string | undefined;
            this.rules.set([]);
            this.rulesLoading.set(true);
            this.ruleFilter.set('all');

            const poster$ =
              brandId && updatedBy
                ? this.businessAccountService.getAccountUsers(brandId).pipe(
                    take(1),
                    map((users) => posterName(users, updatedBy)),
                    catchError(() => of(null)),
                  )
                : of(null);

            const docs$ =
              docIds.length > 0
                ? this.complianceDocsService.getComplianceDocs(docIds).pipe(
                    take(1),
                    map((docs) => asComplianceDocs(docs)),
                    catchError(() => of<ComplianceFact[]>([])),
                  )
                : of<ComplianceFact[]>([]);

            const questions$ = isFindBriefContent(brief.monitorType)
              ? of<NoodleQuestionDto[]>([])
              : this.noodleService.getBriefQuestions(brief.id).pipe(
                  take(1),
                  map((group) => questionsFromGroup(group)),
                  catchError(() => of<NoodleQuestionDto[]>([])),
                );

            return forkJoin({ poster: poster$, docs: docs$, questions: questions$ });
          }),
        )
        .subscribe(({ poster, docs, questions }) => {
          this.postedByName.set(poster);
          this.compliance.set(docs);
          this.manageBriefTabsMonitorService.selectedComplianceDocs.set(docs);
          this.rules.set(questions);
          this.rulesLoading.set(false);
        }),
    );
  }

  setFilter(filter: ExtractedRuleFilter): void {
    this.ruleFilter.set(filter);
  }

  filterChipClass(id: ExtractedRuleFilter): string {
    return `inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 dark:focus-visible:outline-gray-200 ${extractedRuleFilterClass(id === this.ruleFilter())}`;
  }

  filterCountClass(id: ExtractedRuleFilter): string {
    return `rounded-full px-2 py-0.5 text-xs font-bold ${extractedRuleCountClass(id === this.ruleFilter())}`;
  }

  ruleIconClass(rule: NoodleQuestionDto): string {
    return rule.type === 'do'
      ? 'grid size-7 shrink-0 place-items-center rounded bg-green-500/10 text-green-700 dark:text-green-400'
      : 'grid size-7 shrink-0 place-items-center rounded bg-red-500/10 text-red-600 dark:text-red-400';
  }

  ruleKindClass(rule: NoodleQuestionDto): string {
    return rule.subjectiveType === 1
      ? 'rounded px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide bg-brand-studio/10 text-brand-studio-dark dark:text-brand-studio'
      : 'rounded px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide bg-teal-500/10 text-teal-700 dark:text-teal-300';
  }

  filterAccessibleLabel(chip: ExtractedRuleChip): string {
    return `${chip.label}, ${chip.count}`;
  }

  ruleTag(rule: NoodleQuestionDto): string {
    return extractedRuleTag(rule);
  }

  ruleNumberLabel(index: number): string {
    return extractedRuleNumberLabel(index);
  }

  hasContext(rule: NoodleQuestionDto): boolean {
    return extractedRuleHasContext(rule);
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }
}

function posterName(users: CampaignManagerDto[], updatedBy: string): string | null {
  const match = users.find((user) => accountOf(user).firebaseUid === updatedBy);
  if (!match) {
    return null;
  }
  const account = accountOf(match);
  const name = [account.firstName, account.lastName].filter((part) => !!part?.trim()).join(' ');
  return name.length > 0 ? name : null;
}

function accountOf(user: CampaignManagerDto): { firebaseUid?: string; firstName?: string; lastName?: string } {
  return user.userAccount as { firebaseUid?: string; firstName?: string; lastName?: string };
}

function asComplianceDocs(value: unknown): ComplianceFact[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return [];
    }
    const record = item as { id?: unknown; complianceName?: unknown; complianceDocumentUrl?: unknown };
    if (typeof record.id !== 'string') {
      return [];
    }
    return [
      {
        id: record.id,
        complianceName: typeof record.complianceName === 'string' ? record.complianceName : null,
        complianceDocumentUrl: typeof record.complianceDocumentUrl === 'string' ? record.complianceDocumentUrl : null,
      },
    ];
  });
}
